import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight, Trophy, Linkedin } from 'lucide-react';
import { apiClient } from '../../api/client';
import useAdultMode from '../../hooks/useAdultMode';

/**
 * SecureVaultCard — Hero-level dashboard card for the teen Secure Vault.
 *
 * Designed to be the most visually distinctive card on the dashboard:
 *  - Dark obsidian gradient with a vault-door SVG accent (matches /vault-demo).
 *  - Pulsing combination dial that animates while locked.
 *  - Live trophy count, subscriber tenure, and days-until-unlock from
 *    `/api/memes/gallery` (graceful fallback if logged-out).
 *  - One-tap CTA → `/memes` (the teen's actual Vault page).
 *
 * The Vault must FEEL precious — this is where every meme, badge, and NFT
 * the teen earns over 6 months lives, time-locked until they qualify.
 */

const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';
const GOLD = '#FFD700';

const VaultDoor = ({ unlocked }) => (
  <div
    className="relative flex-shrink-0"
    style={{ width: 96, height: 96 }}
    aria-hidden="true"
    data-testid="vault-card-door"
  >
    <svg viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.4))' }}>
      <circle cx="110" cy="110" r="106" fill="#1B1B1B" stroke="#2E2E2E" strokeWidth="2" />
      <circle cx="110" cy="110" r="96" fill="#0F0F0F" stroke="#3A3A3A" strokeWidth="1" />
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i * 30) * (Math.PI / 180);
        return <circle key={i} cx={110 + 88 * Math.cos(a)} cy={110 + 88 * Math.sin(a)} r="3" fill="#4A4A4A" />;
      })}
      <circle cx="110" cy="110" r="64" fill="#161616" stroke="#3A3A3A" strokeWidth="1" />
      <g className="vault-card-dial">
        <circle cx="110" cy="110" r="48" fill="none" stroke={unlocked ? GOLD : PURPLE} strokeWidth="2.5" opacity="0.7" />
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i * 15) * (Math.PI / 180) - Math.PI / 2;
          const isMaj = i % 6 === 0;
          return (
            <line
              key={i}
              x1={110 + (isMaj ? 38 : 40) * Math.cos(a)}
              y1={110 + (isMaj ? 38 : 40) * Math.sin(a)}
              x2={110 + 44 * Math.cos(a)}
              y2={110 + 44 * Math.sin(a)}
              stroke={isMaj ? (unlocked ? GOLD : PURPLE) : '#5A5A5A'}
              strokeWidth={isMaj ? 1.6 : 0.6}
            />
          );
        })}
      </g>
      <circle cx="110" cy="110" r="32" fill="#0A0A0A" stroke="#3A3A3A" strokeWidth="1" />
      {unlocked ? (
        <path d="M96 110L106 120L126 100" stroke={GOLD} strokeWidth="4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <g transform="translate(110, 110)">
          <rect x="-9" y="-2" width="18" height="14" rx="2" fill={PURPLE} />
          <path d="M-6 -2V-7A6 6 0 016 -7V-2" stroke={PURPLE} strokeWidth="2.2" fill="none" strokeLinecap="round" />
        </g>
      )}
    </svg>
    <style>{`
      .vault-card-dial {
        transform-origin: 110px 110px;
        animation: vault-card-spin 18s linear infinite;
      }
      @keyframes vault-card-spin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .vault-card-dial { animation: none; }
      }
    `}</style>
  </div>
);

const SecureVaultCard = () => {
  const navigate = useNavigate();
  const isAdultMode = useAdultMode();
  const [data, setData] = useState({
    total: 0,
    subscriber_days: 0,
    days_until_unlock: 180,
    tenure_eligible: false,
    vault_verified: false,
    unminted_count: 0,
    minted_count: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiClient.request('/api/memes/gallery')
      .then((res) => {
        if (cancelled || !res) return;
        setData({
          total: res.total || 0,
          subscriber_days: res.subscriber_days || 0,
          days_until_unlock: res.days_until_unlock ?? 180,
          tenure_eligible: !!res.tenure_eligible,
          vault_verified: !!res.vault_verified,
          unminted_count: res.unminted_count || 0,
          minted_count: res.minted_count || 0,
        });
      })
      .catch(() => { /* silent — show locked default */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const unlocked = data.tenure_eligible && data.vault_verified;
  const pct = Math.max(0, Math.min(100, Math.round(((180 - data.days_until_unlock) / 180) * 100)));

  return (
    <button
      onClick={() => navigate('/memes')}
      className="w-full text-left rounded-3xl p-5 sm:p-6 relative overflow-hidden transition-transform hover:scale-[1.005] active:scale-[0.995]"
      style={{
        background: 'linear-gradient(135deg, #1A0F2E 0%, #2A1A4E 50%, #3D1F5C 100%)',
        boxShadow: '0 10px 40px rgba(123, 79, 216, 0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
        border: `1px solid ${PURPLE}55`,
      }}
      data-testid="secure-vault-card"
      aria-label="Open your Secure Vault"
    >
      {/* Decorative grain / sparkle layer */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255, 215, 0, 0.12), transparent 40%), radial-gradient(circle at 80% 70%, rgba(245, 131, 31, 0.10), transparent 45%)',
          pointerEvents: 'none',
        }}
      />

      <div className="relative flex items-start gap-4 sm:gap-5">
        <VaultDoor unlocked={unlocked} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{
                background: unlocked ? `${GOLD}33` : `${PURPLE}44`,
                color: unlocked ? GOLD : '#D7BBFF',
                border: `1px solid ${unlocked ? GOLD : PURPLE}66`,
              }}
              data-testid="vault-card-status-chip"
            >
              {unlocked ? <Sparkles size={11} /> : <Lock size={11} />}
              {unlocked ? 'Unlocked' : 'Secure Vault'}
            </span>
          </div>

          <h3 className="text-white font-extrabold text-xl sm:text-2xl leading-tight" data-testid="vault-card-title">
            Your Secure Vault
          </h3>
          <p className="text-[13px] sm:text-sm text-white/70 mt-1 mb-3">
            {unlocked
              ? 'Open it up — your trophies are ready to mint.'
              : (isAdultMode
                  ? 'Every core simulation, conversation breakdown & mastery token you earn lives here, time-locked for 6 months.'
                  : 'Every badge, trophy & NFT you earn lives here, time-locked for 6 months.')}
          </p>

          {/* Live stats row */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3">
            <Stat label="Trophies" value={loading ? '—' : data.total} icon={<Trophy size={12} />} highlight />
            <Stat label="Days in" value={loading ? '—' : data.subscriber_days} />
            <Stat
              label={unlocked ? 'Status' : 'Unlocks in'}
              value={loading ? '—' : (unlocked ? 'Ready' : `${data.days_until_unlock}d`)}
              accent={unlocked}
            />
          </div>

          {/* Progress bar to 180 days */}
          {!unlocked && (
            <div className="mb-3" data-testid="vault-card-progress">
              <div className="flex items-center justify-between text-[11px] text-white/60 mb-1">
                <span>Progress to unlock</span>
                <span className="font-semibold text-white/80">{pct}%</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, ${PURPLE}, ${ORANGE})`,
                    boxShadow: `0 0 8px ${ORANGE}88`,
                  }}
                />
              </div>
            </div>
          )}

          <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/95 group">
            <span>{unlocked ? 'Open the Vault' : 'Peek inside'}</span>
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </div>

          {/* Phase C — Adult-only: surface a clean "Add to LinkedIn Profile"
              CTA so minted Mastery Tokens slot straight into a resume's
              Certifications section. Uses LinkedIn's documented
              add-certification share URL (organizationName=HiBFF). */}
          {isAdultMode && unlocked && data.minted_count > 0 && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              <a
                href={`https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent('HiBFF Mastery Token')}&organizationName=HiBFF&issueYear=${new Date().getFullYear()}&issueMonth=${new Date().getMonth() + 1}&certUrl=${encodeURIComponent(`${window.location.origin}/memes`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="linkedin-add-cert"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A66C2] hover:bg-[#0958A8] text-white text-xs font-bold transition"
              >
                <Linkedin size={12} />
                Add to LinkedIn Profile
              </a>
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

const Stat = ({ label, value, icon, highlight, accent }) => (
  <div
    className="rounded-xl px-2.5 py-2"
    style={{
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.08)',
    }}
  >
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold" style={{ color: highlight ? GOLD : 'rgba(255,255,255,0.55)' }}>
      {icon}
      <span>{label}</span>
    </div>
    <div
      className="text-base sm:text-lg font-extrabold mt-0.5"
      style={{ color: accent ? GOLD : '#fff' }}
    >
      {value}
    </div>
  </div>
);

export default SecureVaultCard;
